import { getAddToCartParamsFromUrl } from '@/utils/product';

/**
 * Composable for handling add-to-cart functionality triggered by URL parameters.
 */
export function useAddToCartFromUrl() {
  // Lazy initialization - only get these when actually needed (client-side only)
  let nuxtApp = null;
  let store = null;

  function getContext() {
    if (!nuxtApp) nuxtApp = useNuxtApp();
    if (!store) store = nuxtApp.$store;
    return { nuxtApp, store };
  }

  /**
   * Clears the atc and sku query parameters from the URL
   */
  function clearAtcQueryParams() {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.delete('atc');
    url.searchParams.delete('sku');
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Handles global add-to-cart (when no product data is available).
   * Fetches the product by SKU and adds it to cart.
   */
  async function handleGlobalAddToCart(params) {
    if (params.action !== 'addBySku') return false;

    try {
      const { nuxtApp, store } = getContext();
      const result = await nuxtApp.$shopifyProductBySku(params.sku);

      if (!result) {
        console.warn(`Auto-ATC: Product with SKU '${params.sku}' not found`);
        return false;
      }

      const { product, variant } = result;

      if (!variant.available) {
        console.warn(`Auto-ATC: Variant with SKU '${params.sku}' is not available`);
        return false;
      }

      if (nuxtApp.$dlPush) {
        nuxtApp.$dlPush.eECaddToCart(variant, product, params.quantity);
      }

      await store.dispatch('cart/addLineItems', [
        {
          merchandiseId: variant.id,
          quantity: params.quantity,
          attributes: [
            { key: '_maxQuantity', value: '0' },
            { key: '_isCourse', value: 'false' },
          ],
        },
      ]);

      store.dispatch('cart/open');
      clearAtcQueryParams();

      return true;
    } catch (error) {
      console.error('Auto-ATC error:', error);
      return false;
    }
  }

  /**
   * Handles PDP add-to-cart (when product data is already available).
   *
   * @param {Object} params - The parsed URL params
   * @param {Object} options - PDP-specific options
   * @param {Array} options.variants - Available product variants
   * @param {Object} options.selectedVariant - Currently selected variant
   * @param {boolean} options.availableForSale - Whether the product is available
   * @param {Function} options.addToCart - Function to add to cart (handles addOn, isCourse, etc.)
   * @param {Function} options.setVariant - Function to set the selected variant
   */
  async function handlePdpAddToCart(params, options) {
    const { variants, selectedVariant, availableForSale, addToCart, setVariant } = options;

    if (params.action === 'selectedVariant') {
      const variant = variants.find((v) => v.sku === params.sku);

      if (variant && variant.available) {
        if (setVariant) {
          setVariant(variant);
        }
        await addToCart(params.quantity);
        clearAtcQueryParams();
        return true;
      }

      console.warn(`Auto-ATC: Variant with SKU '${params.sku}' not found or unavailable`);
      return false;
    }

    if (params.action === 'firstVariant') {
      if (availableForSale) {
        await addToCart(params.quantity);
        clearAtcQueryParams();
        return true;
      }

      console.warn('Auto-ATC: Product is not available for sale');
      return false;
    }

    return false;
  }

  /**
   * Main entry point - parses URL and handles add-to-cart based on target.
   *
   * For global target: fetches product and adds to cart
   * For pdp target: requires options with product data
   *
   * @param {Object} pdpOptions - Optional PDP-specific options (required for pdp target)
   */
  async function handleAddToCartFromUrl(pdpOptions = null) {
    const params = getAddToCartParamsFromUrl();
    if (!params) return false;

    if (params.target === 'global') {
      return handleGlobalAddToCart(params);
    }

    if (params.target === 'pdp' && pdpOptions) {
      return handlePdpAddToCart(params, pdpOptions);
    }

    return false;
  }

  return {
    getAddToCartParamsFromUrl,
    handleAddToCartFromUrl,
    handleGlobalAddToCart,
    handlePdpAddToCart,
    clearAtcQueryParams,
  };
}
